import { ReactNode, isValidElement, useEffect, useRef } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { reducedMotionStepVariants, stepVariants } from '../animations';

type WizardStepTransitionProps = {
  stepKey: string;
  direction: 1 | -1;
  initial?: boolean;
  children: ReactNode;
};

// A step's own re-renders (e.g. typing into a field on the current step) always produce a new
// `children` object by reference, so comparing `children` itself would false-positive on every
// keystroke. Comparing the rendered element's *type* instead is stable across a step's own
// re-renders but still differs if a genuinely different step ever ends up sharing a stepKey.
function getChildType(children: ReactNode): unknown {
  return isValidElement(children) ? children.type : typeof children;
}

export function WizardStepTransition({
  stepKey,
  direction,
  initial,
  children,
}: WizardStepTransitionProps) {
  const shouldReduceMotion = useReducedMotion();
  const containerRef = useRef<HTMLDivElement>(null);
  const previousRenderRef = useRef<{ stepKey: string; childType: unknown } | null>(null);

  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') {
      const childType = getChildType(children);
      const previousRender = previousRenderRef.current;
      if (
        previousRender &&
        previousRender.stepKey === stepKey &&
        previousRender.childType !== childType
      ) {
        // eslint-disable-next-line no-console
        console.warn(
          `WizardStepTransition received the same stepKey ("${stepKey}") for what looks like ` +
            'a different step. AnimatePresence keys content by stepKey, so this step change ' +
            'will not animate. Give each distinct step/sub-step its own stepKey.',
        );
      }
      previousRenderRef.current = { stepKey, childType };
    }
  }, [stepKey, children]);

  const focusStepContainer = () => {
    const container = containerRef.current;
    if (!container) return;
    const focusTarget = container.querySelector<HTMLElement>('h1, h2, [data-autofocus]');
    (focusTarget ?? container).focus();
  };

  return (
    <AnimatePresence mode="wait" custom={direction} initial={initial ?? false}>
      <motion.div
        key={stepKey}
        ref={containerRef}
        custom={direction}
        variants={shouldReduceMotion ? reducedMotionStepVariants : stepVariants}
        initial="initial"
        animate="animate"
        exit="exit"
        onAnimationComplete={focusStepContainer}
        tabIndex={-1}
        style={{ width: '100%' }}
      >
        {children}
      </motion.div>
    </AnimatePresence>
  );
}
